import { useState, useEffect, type FormEvent } from "react";
import { useSignUp, useAuth } from "@clerk/react";
import { Link, useNavigate } from "@tanstack/react-router";
import { cn } from "@/lib/utils";
import { ArrowRight, UserPlus } from "lucide-react";
import "./sign-up.css";

type Step = "form" | "verifying";

export default function SignUpPage() {
  const { signUp, errors, fetchStatus } = useSignUp();
  const { isSignedIn } = useAuth();
  const navigate = useNavigate();
  const [revealed, setRevealed] = useState(false);
  const [step, setStep] = useState<Step>("form");
  const [code, setCode] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    requestAnimationFrame(() => setRevealed(true));
  }, []);

  useEffect(() => {
    if (isSignedIn) {
      navigate({ to: "/profile" });
    }
  }, [isSignedIn, navigate]);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setFormError(null);

    const form = new FormData(e.currentTarget);
    const emailAddress = (form.get("email") as string).trim();
    const password = form.get("password") as string;
    const firstName = (form.get("firstName") as string).trim() || undefined;
    const lastName = (form.get("lastName") as string).trim() || undefined;

    const { error } = await signUp.password({ emailAddress, password, firstName, lastName });

    if (error) return;

    if (
      signUp.status === "missing_requirements" &&
      signUp.unverifiedFields.includes("email_address")
    ) {
      await signUp.verifications.sendEmailCode();
      setStep("verifying");
      return;
    }

    if (signUp.status === "complete") {
      await finalize();
    }
  }

  async function handleVerify(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setFormError(null);

    const { error } = await signUp.verifications.verifyEmailCode({ code });

    if (error) return;

    if (signUp.status === "complete") {
      await finalize();
    }
  }

  async function finalize() {
    await signUp.finalize({
      navigate: ({ decorateUrl }) => {
        const url = decorateUrl("/profile");
        if (url.startsWith("http")) {
          window.location.href = url;
        } else {
          navigate({ to: "/profile" });
        }
      },
    });
  }

  if (isSignedIn || signUp.status === "complete") return null;

  const isBusy = fetchStatus === "fetching";

  return (
    <div className="auth-page">
      <section className={cn("auth-section", revealed && "revealed")}>
        <div className="auth-card">
          {step === "verifying" ? (
            <>
              <div className="auth-header">
                <h1 className="auth-heading">Check your email</h1>
                <p className="auth-subtext">
                  We sent a verification code to your email address.
                </p>
              </div>

              <form className="auth-form" onSubmit={handleVerify}>
                <div className="auth-field">
                  <label className="auth-label" htmlFor="auth-code">
                    Verification code
                  </label>
                  <input
                    id="auth-code"
                    type="text"
                    className="auth-input"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder="Enter 6-digit code"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    required
                  />
                  {errors?.fields?.code && (
                    <p className="auth-field-error">{errors.fields.code.message}</p>
                  )}
                </div>

                {formError && <div className="auth-error">{formError}</div>}
                {errors?.global?.map((err, i) => (
                  <div key={i} className="auth-error">{err.message}</div>
                ))}

                <button type="submit" className="auth-submit" disabled={isBusy}>
                  {isBusy ? "Verifying..." : "Verify"}
                  {!isBusy && <ArrowRight className="size-4" />}
                </button>

                <button
                  type="button"
                  className="auth-link-btn"
                  onClick={() => signUp.verifications.sendEmailCode()}
                >
                  Resend code
                </button>
              </form>
            </>
          ) : (
            <>
              <div className="auth-header">
                <UserPlus className="auth-icon" />
                <h1 className="auth-heading">Create your account</h1>
                <p className="auth-subtext">
                  Join Tomorrow&apos;s Run to track your race history.
                </p>
              </div>

              <form className="auth-form" onSubmit={handleSubmit}>
                <div className="auth-name-row">
                  <div className="auth-field">
                    <label className="auth-label" htmlFor="auth-first-name">
                      First name <span className="auth-optional">(optional)</span>
                    </label>
                    <input
                      id="auth-first-name"
                      name="firstName"
                      type="text"
                      className="auth-input"
                      autoComplete="given-name"
                    />
                  </div>
                  <div className="auth-field">
                    <label className="auth-label" htmlFor="auth-last-name">
                      Last name <span className="auth-optional">(optional)</span>
                    </label>
                    <input
                      id="auth-last-name"
                      name="lastName"
                      type="text"
                      className="auth-input"
                      autoComplete="family-name"
                    />
                  </div>
                </div>

                <div className="auth-field">
                  <label className="auth-label" htmlFor="auth-email">
                    Email address
                  </label>
                  <input
                    id="auth-email"
                    name="email"
                    type="email"
                    className="auth-input"
                    placeholder="you@example.com"
                    autoComplete="email"
                    required
                  />
                  {errors?.fields?.emailAddress && (
                    <p className="auth-field-error">{errors.fields.emailAddress.message}</p>
                  )}
                </div>

                <div className="auth-field">
                  <label className="auth-label" htmlFor="auth-password">
                    Password
                  </label>
                  <input
                    id="auth-password"
                    name="password"
                    type="password"
                    className="auth-input"
                    placeholder="Create a password"
                    autoComplete="new-password"
                    required
                  />
                  {errors?.fields?.password && (
                    <p className="auth-field-error">{errors.fields.password.message}</p>
                  )}
                </div>

                {formError && <div className="auth-error">{formError}</div>}
                {errors?.global?.map((err, i) => (
                  <div key={i} className="auth-error">{err.message}</div>
                ))}

                <button type="submit" className="auth-submit" disabled={isBusy}>
                  {isBusy ? "Creating account..." : "Create account"}
                  {!isBusy && <ArrowRight className="size-4" />}
                </button>

                <div id="clerk-captcha" />

                <p className="auth-footer-text">
                  Already have an account?{" "}
                  <Link to="/sign-in" className="auth-link">
                    Sign in
                  </Link>
                </p>
              </form>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
